/**
 * M365EmailConfig Component
 * Displays M365 email authentication status and configuration
 * Similar to OneDriveCard but for email OAuth
 */

import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, LogOut, Loader } from 'lucide-react';
import * as m365EmailService from '../../services/m365EmailService';
import { getNetlifyFunctionUrl } from '../../config/netlify';
import './M365EmailConfig.css';

const M365EmailConfig = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
    
    // Listen for OAuth callback messages from popup window
    const handleMessage = async (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'm365-email-auth-success') {
        setAuthenticating(true);
        setError(null);
        
        try {
          await m365EmailService.handleCallback(event.data.code);
          await checkAuthStatus();
          
          // Close the popup window
          if (event.source && typeof event.source.close === 'function') {
            event.source.close();
          }
        } catch (err) {
          console.error('Error handling OAuth callback:', err);
          setError('Error al completar autenticación: ' + err.message);
        } finally {
          setAuthenticating(false);
        }
      } else if (event.data.type === 'm365-email-auth-error') {
        setError(`Error de autenticación: ${event.data.errorDescription || event.data.error}`);
        setAuthenticating(false);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const checkAuthStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const authenticated = await m365EmailService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        const info = await m365EmailService.getAccountInfo();
        setAccountInfo(info);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setError('Error al verificar estado de autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setAuthenticating(true);
    setError(null);
    
    try {
      // Get the auth URL from the backend
      const functionUrl = getNetlifyFunctionUrl('m365-email-auth');
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'authenticate',
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate authentication URL');
      }

      if (data.authUrl) {
        // Open popup window for OAuth flow
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        window.open(
          data.authUrl,
          'M365 Email Authentication',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );
      } else {
        throw new Error('No authentication URL returned');
      }
    } catch (err) {
      console.error('Error initiating authentication:', err);
      setError('Error al iniciar autenticación: ' + err.message);
      setAuthenticating(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Está seguro de desconectar su cuenta de correo M365?')) {
      return;
    }

    setDisconnecting(true);
    setError(null);
    
    try {
      await m365EmailService.disconnect();
      setIsAuthenticated(false);
      setAccountInfo(null);
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Error al desconectar: ' + err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="m365-email-card">
        <div className="m365-email-card-loading">
          <Loader size={24} className="spinning" />
          <span>Verificando conexión...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="m365-email-card">
      <div className="m365-email-card-header">
        <div className="m365-email-card-title">
          <Mail size={24} />
          <h3>Cuenta de Correo M365</h3>
        </div>
        <div className={`m365-email-status ${isAuthenticated ? 'connected' : 'disconnected'}`}>
          {isAuthenticated ? (
            <>
              <CheckCircle size={18} />
              <span>Conectado</span>
            </>
          ) : (
            <>
              <XCircle size={18} />
              <span>Desconectado</span>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="m365-email-error">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="m365-email-card-content">
        {isAuthenticated && accountInfo ? (
          <>
            <div className="m365-email-info-grid">
              <div className="m365-email-info-item">
                <label>Cuenta conectada:</label>
                <span>{accountInfo.accountEmail}</span>
              </div>
              
              <div className="m365-email-info-item">
                <label>Fecha de conexión:</label>
                <span>{formatDate(accountInfo.connectedAt)}</span>
              </div>
            </div>

            <div className="m365-email-description">
              <p>Esta cuenta se utiliza para enviar correos electrónicos desde el sistema, incluyendo:</p>
              <ul>
                <li>Correos manuales de preparativos (Solicitud de Embarque, Coordinar Transporte, etc.)</li>
                <li>Reenvío automático de correos pendientes</li>
                <li>Envío de documentos adjuntos</li>
              </ul>
            </div>

            <button
              className="m365-email-disconnect-button"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? (
                <>
                  <Loader size={18} className="spinning" />
                  <span>Desconectando...</span>
                </>
              ) : (
                <>
                  <LogOut size={18} />
                  <span>Desconectar cuenta</span>
                </>
              )}
            </button>
          </>
        ) : (
          <div className="m365-email-not-connected">
            <p>No hay ninguna cuenta de correo M365 conectada.</p>
            <p className="m365-email-description">
              Conecte su cuenta de Microsoft 365 para habilitar el envío de correos electrónicos desde el sistema.
            </p>
            <button
              className="m365-email-connect-button"
              onClick={handleConnect}
              disabled={authenticating}
            >
              {authenticating ? (
                <>
                  <Loader size={18} className="spinning" />
                  <span>Conectando...</span>
                </>
              ) : (
                <>
                  <Mail size={18} />
                  <span>Conectar con M365</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default M365EmailConfig;
